import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, inr, resolveImg, idemKey, bidzChanged } from "../lib/api";
import { useAuth } from "../lib/auth";
import Countdown, { useCountdown } from "../components/Countdown";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Heart, ShieldCheck, Truck, BadgeCheck, Gavel, AlertCircle, Coins } from "lucide-react";
import { toast } from "sonner";
import { BidzBidSection } from "../components/BidzBidSection";
import { Est } from "../lib/currency";

export default function AuctionDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [bids, setBids] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [watching, setWatching] = useState(false);
  const [bidOpen, setBidOpen] = useState(false);
  const [bidAmount, setBidAmount] = useState(0);
  const [quote, setQuote] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const bidKey = useRef(idemKey());

  const loadAll = useCallback(() => {
    api.get(`/listings/${id}`).then(r => {
      setListing(r.data);
      setBidAmount((r.data.current_bid || r.data.starting_bid) + r.data.min_increment);
    }).catch(e => {
      if (e?.response?.status === 404) setNotFound(true);
      else toast.error("Could not load auction");
    });
    api.get(`/listings/${id}/bids`).then(r => setBids(r.data)).catch(() => setBids([]));
  }, [id]);
  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    if (!user) return;
    api.get("/watchlist").then(r => setWatching(r.data.some(l => l.id === id))).catch(() => {});
  }, [user, id]);

  const ms = useCountdown(listing?.ends_at || new Date().toISOString());
  const ended = ms <= 0;

  const openBid = async () => {
    if (!user) { nav("/signin?next=" + encodeURIComponent(`/auction/${id}`)); return; }
    const minNext = (listing.current_bid || listing.starting_bid) + (listing.current_bid > 0 ? listing.min_increment : 0);
    setBidAmount(minNext);
    setBidOpen(true);
    try {
      const r = await api.post(`/listings/${id}/quote`, { amount: minNext });
      setQuote(r.data);
    } catch (e) {
      setQuote(null);
      toast.error("Could not calculate total", { description: e?.response?.data?.detail || "Please try again." });
    }
  };

  const updateQuote = async (amt) => {
    setBidAmount(amt);
    bidKey.current = idemKey();
    try {
      const r = await api.post(`/listings/${id}/quote`, { amount: Number(amt) });
      setQuote(r.data);
    } catch {
      setQuote(null);
    }
  };

  const confirmBid = async () => {
    setPlacing(true);
    try {
      await api.post(`/listings/${id}/bid`, { amount: Number(bidAmount) }, { headers: { "Idempotency-Key": bidKey.current } });
      bidKey.current = idemKey();
      bidzChanged();
      toast.success("Bid placed", { description: `Your ${inr(bidAmount)} bid is now winning.${quote?.bidz?.charge ? ` ${quote.bidz.charge} Bidz used.` : quote?.bidz?.has_pass ? " Covered by your Auction Pass." : ""}` });
      setBidOpen(false);
      loadAll();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error("Bid failed", { description: (typeof d === "string" ? d : d?.message) || "Please try again." });
      if (d?.code === "insufficient_bidz") updateQuote(bidAmount);
    } finally { setPlacing(false); }
  };

  const toggleWatch = async () => {
    if (!user) { nav("/signin"); return; }
    if (watching) { await api.delete(`/watchlist/${id}`); setWatching(false); }
    else { await api.post(`/watchlist/${id}`); setWatching(true); toast.success("Added to watchlist"); }
  };

  if (notFound) return (
    <div data-testid="auction-not-found" className="max-w-7xl mx-auto px-4 py-24 text-center">
      <h1 className="text-2xl font-extrabold">This auction is no longer available</h1>
      <p className="mt-2 text-slate-500">It may have been removed or never existed.</p>
      <Link to="/auctions" className="mt-6 inline-block text-amber-600 font-semibold" data-testid="auction-not-found-browse-link">Browse live auctions</Link>
    </div>
  );
  if (!listing) return <div className="max-w-7xl mx-auto px-4 py-16 text-center text-slate-500">Loading…</div>;

  const currentBid = listing.current_bid || listing.starting_bid;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
      <nav className="text-xs text-slate-500 mb-4">
        <Link to="/">Home</Link> / <Link to="/auctions">Auctions</Link> / <Link to={`/auctions?category_id=${listing.category_id}`}>{listing.category_name}</Link>
      </nav>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* GALLERY */}
        <div className="lg:col-span-7">
          <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-slate-100 dark:bg-slate-800 mb-3">
            {listing.photos?.[activeImg] && <img src={resolveImg(listing.photos[activeImg])} className="h-full w-full object-cover" alt={listing.title} />}
          </div>
          <div className="flex gap-2 overflow-x-auto">
            {listing.photos?.map((p, i) => (
              <button key={p} onClick={() => setActiveImg(i)} className={`h-20 w-20 rounded-lg overflow-hidden border-2 flex-shrink-0 ${i === activeImg ? "border-amber-500" : "border-transparent"}`}>
                <img src={resolveImg(p)} className="h-full w-full object-cover" alt="" />
              </button>
            ))}
          </div>

          <Tabs defaultValue="description" className="mt-8">
            <TabsList>
              <TabsTrigger value="description">Description</TabsTrigger>
              <TabsTrigger value="specs">Specifications</TabsTrigger>
              <TabsTrigger value="bids" data-testid="tab-bids">Bid history ({listing.bid_count})</TabsTrigger>
              <TabsTrigger value="shipping">Shipping & Protection</TabsTrigger>
            </TabsList>
            <TabsContent value="description" className="mt-4 text-slate-700 dark:text-slate-300 leading-relaxed">{listing.description}</TabsContent>
            <TabsContent value="specs" className="mt-4">
              <dl className="grid grid-cols-2 gap-y-2 text-sm">
                <dt className="text-slate-500">Condition</dt><dd className="font-semibold">{listing.condition}</dd>
                {listing.brand && <><dt className="text-slate-500">Brand</dt><dd className="font-semibold">{listing.brand}</dd></>}
                <dt className="text-slate-500">Category</dt><dd className="font-semibold">{listing.category_name}</dd>
                <dt className="text-slate-500">Reserve</dt><dd className="font-semibold">{listing.reserve_price ? "Yes" : "No reserve"}</dd>
                <dt className="text-slate-500">Min. increment</dt><dd className="font-semibold">{inr(listing.min_increment)}</dd>
              </dl>
            </TabsContent>
            <TabsContent value="bids" className="mt-4">
              {bids.length === 0 ? <div className="text-sm text-slate-500">No bids yet — be the first.</div> : (
                <ul className="divide-y divide-slate-200 dark:divide-slate-800">
                  {bids.map(b => (
                    <li key={b.id} className="flex items-center justify-between py-2 text-sm">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-slate-200 dark:bg-slate-700 grid place-items-center text-xs font-bold">{b.bidder_alias.slice(-2)}</div>
                        <div>
                          <div className="font-semibold">{b.bidder_alias}</div>
                          <div className="text-xs text-slate-500">{new Date(b.created_at).toLocaleString()}</div>
                        </div>
                      </div>
                      <div className={`font-bold ${b.is_winning ? "text-emerald-600" : ""}`}>{inr(b.amount)}</div>
                    </li>
                  ))}
                </ul>
              )}
            </TabsContent>
            <TabsContent value="shipping" className="mt-4 space-y-3 text-sm">
              <div className="flex items-center gap-2"><Truck className="h-4 w-4 text-amber-600" /> {listing.shipping_method} — {inr(listing.shipping_price)}</div>
              <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Escrow protected: funds held until you accept the item</div>
              <div className="flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-emerald-600" /> Verified seller</div>
            </TabsContent>
          </Tabs>
        </div>

        {/* BID PANEL */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-24">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">{listing.category_name}</div>
            <h1 className="mt-2 text-2xl md:text-3xl font-extrabold leading-tight text-slate-900 dark:text-white">{listing.title}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border border-emerald-200"><BadgeCheck className="h-3 w-3 mr-1" /> Verified Seller</Badge>
              <Badge variant="outline">{listing.seller_name}</Badge>
              <Badge variant="outline" className="capitalize">{listing.condition.replace("_", " ")}</Badge>
            </div>

            <div className="mt-6 rounded-2xl bg-slate-900 text-white p-6 shadow-2xl border border-slate-800 relative overflow-hidden">
              <div className="absolute inset-0 grain-bg opacity-30" />
              <div className="relative">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-amber-400 font-bold">Current Bid</div>
                  <Countdown endsAt={listing.ends_at} testid="auction-countdown-timer" />
                </div>
                <div data-testid="auction-current-bid" className="mt-2 text-4xl font-extrabold">{inr(currentBid)}</div>
                <Est inr={currentBid} className="text-slate-300" />
                <div className="mt-1 text-xs text-slate-400">{listing.bid_count} bids · {listing.watchers} watching</div>
                {listing.bidz_cost && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 text-amber-300 px-3 py-1 text-xs font-bold" data-testid="auction-bidz-cost">
                      <Coins className="h-3.5 w-3.5" /> {listing.bidz_cost} Bidz per bid
                    </span>
                    {listing.pass_price && (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 text-slate-200 px-3 py-1 text-xs font-bold" data-testid="auction-pass-price">
                        or Auction Pass {inr(listing.pass_price)} <Est inr={listing.pass_price} />
                      </span>
                    )}
                  </div>
                )}

                {ended ? (
                  <div className="mt-5 p-3 rounded-lg bg-slate-800 text-sm">Auction has ended.</div>
                ) : (
                  <>
                    <Button
                      data-testid="place-bid-button"
                      onClick={openBid}
                      size="lg"
                      className="mt-5 w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold text-base rounded-xl"
                    >
                      <Gavel className="h-4 w-4 mr-2" /> PLACE BID
                    </Button>
                    <button data-testid="watch-button" onClick={toggleWatch} className="mt-3 w-full inline-flex items-center justify-center gap-2 text-sm text-slate-300 hover:text-white">
                      <Heart className={`h-4 w-4 ${watching ? "fill-red-500 text-red-500" : ""}`} /> {watching ? "Watching" : "Add to Watchlist"}
                    </button>
                    <div className="mt-4 text-[11px] text-slate-400 flex items-start gap-2">
                      <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                      Bids are binding. See the exact total before confirming.
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              <TrustCell label="Buyer Protection" value={`${listing.buyer_fee_pct}%`} />
              <TrustCell label="Seller Fee" value={`${listing.seller_fee_pct}%`} />
              <TrustCell label="Shipping" value={inr(listing.shipping_price)} />
            </div>
          </div>
        </div>
      </div>

      {/* BID CONFIRM MODAL */}
      <Dialog open={bidOpen} onOpenChange={setBidOpen}>
        <DialogContent data-testid="bid-confirm-modal" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Place a binding bid</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-xs text-amber-600 font-bold uppercase tracking-wider">This is a binding bid</div>
            <div>
              <label className="text-xs text-slate-500 font-semibold">Your bid</label>
              <Input data-testid="bid-input-amount" type="number" value={bidAmount} onChange={(e) => updateQuote(e.target.value)} className="text-2xl font-extrabold h-14 mt-1" />
              <div className="text-[11px] text-slate-500 mt-1">Minimum next bid: {inr((listing.current_bid || listing.starting_bid) + (listing.current_bid > 0 ? listing.min_increment : 0))}</div>
            </div>
            <div className="rounded-xl bg-slate-50 dark:bg-slate-800 p-4 space-y-2 text-sm">
              <Row label="Winning bid" value={inr(bidAmount)} />
              <Row label={`Buyer Protection (${listing.buyer_fee_pct}%)`} value={inr(quote?.buyer_fee || 0)} />
              <Row label="Shipping" value={inr(quote?.shipping ?? listing.shipping_price)} />
              <Row label={`Taxes on fees`} value={inr(quote?.tax || 0)} />
              <div className="border-t border-slate-200 dark:border-slate-700 pt-2 mt-2 flex justify-between font-extrabold text-base">
                <span>Estimated total if you win</span>
                <span data-testid="bid-estimated-total">{inr(quote?.buyer_total || bidAmount)}</span>
              </div>
              <div className="text-right"><Est inr={quote?.buyer_total || bidAmount} /></div>
            </div>
            <BidzBidSection bidz={quote?.bidz} listing={listing} onBought={() => updateQuote(bidAmount)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBidOpen(false)}>Cancel</Button>
            <Button data-testid="bid-confirm-submit-button" onClick={confirmBid} disabled={placing || quote?.bidz?.sufficient === false} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold">
              {placing ? "Placing..." : "CONFIRM BID"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between"><span className="text-slate-500">{label}</span><span className="font-semibold">{value}</span></div>;
}
function TrustCell({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-3 bg-white dark:bg-slate-900">
      <div className="text-[10px] uppercase text-slate-500 font-semibold">{label}</div>
      <div className="text-sm font-bold mt-0.5">{value}</div>
    </div>
  );
}
